import React from 'react';
import { fade, makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import InputBase from '@material-ui/core/InputBase';
import Menu from '@material-ui/core/Menu';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import MoreIcon from '@material-ui/icons/MoreVert';
import DrawerButton from './DrawerButton';
import Button from '@material-ui/core/Button';
import { Link } from 'react-router-dom';
import { ReactComponent as MFSLogo } from '../images/MFS_Logo.svg';
import { AccountCircle, Settings, ColorLens, ExitToApp } from '@material-ui/icons';
import { useAuthContext } from 'hooks/useAuthContext';
import { useAppContext } from 'hooks/useAppContext';
import { msalApp } from 'utils/AuthUtils';
import AuthCheck from './Shared/AuthCheck';
import SideMenuItem from './Shared/SideMenuItem';
const useStyles = makeStyles(theme => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
  },
  grow: {
    flexGrow: 1,
  },
  title: {
    display: 'none',
    fontVariant: 'small-caps',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  closeProjectButtonContainer: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: fade(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(3),
      width: 'auto',
    },
  },
  search: {
    display: 'none',
    position:'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: fade(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(3),
      width: 'auto',
    },
  },
  searchIcon: {
    display: 'none',
    width: theme.spacing(7),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 7),
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 200,
    },
  },
  sectionDesktop: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  sectionMobile: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  logo: {
    height: '100%',
    fill: 'white',
    width: "45px",
    verticalAlign: 'middle',
    margin: '0 18px 0 18px'
  }
}));

export default function PrimarySearchAppBar() {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const isMenuOpen = Boolean(anchorEl);

  const { account, role } = useAuthContext();
  const { selectedProject, toggleTheme } = useAppContext();

  const handleProfileMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleThemeToggle = () => {
    toggleTheme();
    handleMenuClose();
  }

  const handleLogout = () => {
    const account = msalApp.getAllAccounts()[0];
    msalApp.logout({account: account}).then(r => {debugger});
  }

  const menuId = 'primary-search-account-menu';

  const RenderMenu = React.forwardRef((props, ref) => (
    role && 
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <SideMenuItem key="Profile" moduleName={`Role: ${role.name}`} handleClick={handleMenuClose} icon={AccountCircle} route="#" ref={ref} />
      <SideMenuItem key="Theme" moduleName="Toggle Theme" handleClick={handleThemeToggle} icon={ColorLens} route="#" />
      <AuthCheck role={role} scope="AdminConfig" action="read">
        <SideMenuItem key="Settings" moduleName="MFS Settings" handleClick={handleMenuClose} icon={Settings} route="/MFSConfiguration" />
      </AuthCheck>
      <SideMenuItem key="Logout" moduleName="Logout" handleClick={handleLogout} icon={ExitToApp} route="#" />
    </Menu>
  ));

  return (
    <div className={classes.grow}>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <DrawerButton />
          <Typography className={classes.title} variant="h6" noWrap>
            <MFSLogo className={classes.logo} />
            Mobile Field Services
          </Typography>
          <div className={classes.search}>
            <div className={classes.searchIcon}>
              <SearchIcon />
            </div>
            <InputBase
              placeholder="Search…"
              classes={{
                root: classes.inputRoot,
                input: classes.inputInput,
              }}
              inputProps={{ 'aria-label': 'search' }}
            />
          </div>
          {selectedProject &&
            <div className={classes.closeProjectButtonContainer}>
              <Button
                variant="contained"
                color="secondary"
                className={classes.button}
                startIcon={<CloseIcon />}
                component={Link}
                to={'/projects'}
                style={{textTransform: "none"}}
              >
                Close Selected Project
              </Button>
            </div>
          }
          <div className={classes.grow} />
          <div className={classes.sectionDesktop}>
            <IconButton
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Typography variant="caption" style={{ marginRight: 4 }}>{account}</Typography>
              <AccountCircle />
            </IconButton>
          </div>
          <div className={classes.sectionMobile}>
            <IconButton
              aria-label="show more"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <MoreIcon />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>
      <RenderMenu />
    </div>
  );
}